import { useMemo } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Group, Stack, Text } from '@mantine/core';
import { Server } from 'lucide-react';
import { StatusDot } from '../ui';
import { colors } from '../../theme/tokens';
import type { RangeVm } from '../../api/types';

/**
 * VMTopology — ReactFlow-rendered VM graph for a deployed range.
 *
 * Bundled lazily (`React.lazy` at the route level) because reactflow is
 * heavy. Node layout is a simple grid arranged by role tier:
 *   row 0 (top):    indexer / sensor / monitor
 *   row 1:          domain controllers / servers
 *   row 2 (bottom): workstations
 *
 * For 12-VM scenarios this is enough; v2.1 can swap in dagre/elk.
 */

const ROLE_ROW: Record<string, 0 | 1 | 2> = {
  'wazuh-indexer': 0,
  'wazuh-manager': 0,
  sensor: 0,
  monitor: 0,
  dc: 1,
  server: 1,
  workstation: 2,
};

interface VMNodeData {
  vm: RangeVm;
}

function VmNode({ data }: NodeProps<VMNodeData>) {
  const { vm } = data;
  const dotState =
    vm.status === 'running'
      ? vm.agentConnected
        ? 'running'
        : 'warning'
      : vm.status === 'unreachable' || vm.status === 'failed'
        ? 'error'
        : 'idle';

  return (
    <div
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${vm.agentConnected ? colors.neon : '#2a2a2d'}`,
        borderRadius: 6,
        padding: '10px 14px',
        minWidth: 180,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: colors.hudCyan }} />
      <Stack gap={6}>
        <Group justify="space-between" gap="xs">
          <Group gap={6}>
            <Server size={12} color={colors.hudCyan} />
            <Text size="xs" c="white" fw={600}>
              {vm.hostname}
            </Text>
          </Group>
          <StatusDot state={dotState} size="xs" />
        </Group>
        <Group gap="md">
          <Text size="xs" c="dimmed">
            CPU <span style={{ color: colors.hudAmber }}>{vm.cpu}%</span>
          </Text>
          <Text size="xs" c="dimmed">
            MEM <span style={{ color: colors.hudAmber }}>{vm.memory}%</span>
          </Text>
        </Group>
      </Stack>
      <Handle type="source" position={Position.Bottom} style={{ background: colors.hudCyan }} />
    </div>
  );
}

const nodeTypes = { vm: VmNode };

export interface VMTopologyProps {
  vms: ReadonlyArray<RangeVm>;
  /** Override the role inference — feed in topology metadata from scenario. */
  rolesByHostname?: Record<string, string>;
}

export function VMTopology({ vms, rolesByHostname }: VMTopologyProps) {
  const { nodes, edges } = useMemo(() => {
    const rows: Array<Array<RangeVm>> = [[], [], []];
    for (const vm of vms) {
      const role = rolesByHostname?.[vm.hostname] ?? inferRole(vm.hostname);
      const row = ROLE_ROW[role] ?? 2;
      rows[row]!.push(vm);
    }

    const nodes: Node<VMNodeData>[] = [];
    const xSpacing = 220;
    const ySpacing = 130;
    rows.forEach((row, rowIdx) => {
      row.forEach((vm, colIdx) => {
        const xOffset = -(row.length - 1) * (xSpacing / 2);
        nodes.push({
          id: vm.hostname,
          position: { x: xOffset + colIdx * xSpacing, y: rowIdx * ySpacing },
          data: { vm },
          type: 'vm',
          draggable: true,
        });
      });
    });

    const edges: Edge[] = [];
    const indexers = rows[0]!.map((vm) => vm.hostname);
    const middle = rows[1]!.map((vm) => vm.hostname);
    const leaves = rows[2]!.map((vm) => vm.hostname);

    // Connect each middle node to every indexer; each leaf to a middle node.
    middle.forEach((mid, mIdx) => {
      indexers.forEach((ix) => {
        edges.push({
          id: `${ix}->${mid}`,
          source: ix,
          target: mid,
          animated: false,
          style: { stroke: '#2a2a2d', strokeWidth: 1 },
        });
      });
      leaves.forEach((leaf, lIdx) => {
        if (lIdx % Math.max(1, middle.length) === mIdx % Math.max(1, middle.length)) {
          edges.push({
            id: `${mid}->${leaf}`,
            source: mid,
            target: leaf,
            animated: false,
            style: { stroke: '#2a2a2d', strokeWidth: 1 },
          });
        }
      });
    });

    return { nodes, edges };
  }, [vms, rolesByHostname]);

  return (
    <div style={{ width: '100%', height: 520, background: colors.deep, borderRadius: 6 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#1f1f23" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

function inferRole(hostname: string): string {
  const lower = hostname.toLowerCase();
  if (lower.includes('indexer') || lower.includes('wazuh')) return 'wazuh-indexer';
  if (lower.includes('dc')) return 'dc';
  if (lower.includes('server') || lower.includes('svr')) return 'server';
  return 'workstation';
}

export default VMTopology;
